/**
 * /login, /pending, /denied (docs/01 §1, docs/05). The pending screen is fed
 * entirely from the session — a pending user matches no RLS policy anywhere.
 */

import { Landmark } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { Button, Card, DeniedState, IconTile } from '../../components/ui/index.js';

/** Short codes set by the auth callback redirect — never raw exception text. */
const SIGN_IN_ERRORS: Record<string, string> = {
  canceled: 'Sign-in was canceled. Try again when you are ready.',
  expired: 'That sign-in attempt expired. Start again.',
  provider: 'Microsoft could not complete the sign-in. Try again.',
  exchange: 'The sign-in could not be completed. Try again — tell IT if it keeps happening.',
  disabled: 'This account has been disabled. Contact IT if you believe this is wrong.',
};

function AuthFrame({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-brand-950 p-4">
      <Card className="w-full max-w-sm text-center">
        {/* The catalogued tile (docs/04 §3a, punch 5.4). This was the second
            hand-rolled copy of it, and its glyph was 26px — outside both §6's
            16-21px band and the brand standard's 16-24px ceiling. */}
        <IconTile icon={<Landmark size={21} />} tone="brand" size="lg" className="mx-auto mb-3" />
        <h1 className="font-display text-display-sm text-ink">PropOS</h1>
        {children}
      </Card>
    </div>
  );
}

export function LoginScreen({ error }: { readonly error?: string }): ReactNode {
  const [params] = useSearchParams();
  const fromCallback = params.get('error');
  const message =
    error ?? (fromCallback === null ? undefined : (SIGN_IN_ERRORS[fromCallback] ?? undefined));
  return (
    <AuthFrame>
      <p className="mt-1 text-body text-ink2">The Keith Corporation property operations</p>
      {message !== undefined ? (
        <p
          role="alert"
          className="mt-3 rounded-ctl bg-red-bg px-3 py-2 text-meta font-medium text-red-fg"
        >
          {message}
        </p>
      ) : null}
      <div className="mt-5">
        <Button
          variant="primary"
          onClick={() => {
            window.location.assign('/api/v1/auth/login');
          }}
        >
          Sign in with Microsoft
        </Button>
      </div>
      <p className="mt-3 text-chip text-ink2">
        Your TKC account signs you in — no separate password.
      </p>
    </AuthFrame>
  );
}

export function PendingScreen({ email }: { readonly email: string }): ReactNode {
  return (
    <AuthFrame>
      <p className="mt-1 text-body text-ink2">
        You're signed in as <strong className="text-ink">{email}</strong>, and your access request
        is with an administrator. You'll get in the moment it's approved.
      </p>
      <div className="mt-5">
        <Button
          onClick={() => {
            window.location.assign('/api/v1/auth/login');
          }}
        >
          Check again
        </Button>
      </div>
    </AuthFrame>
  );
}

export function DeniedScreen(): ReactNode {
  return (
    <div className="mx-auto max-w-lg p-6">
      <DeniedState requestAccessEmail="it@thekeithcorp.com" />
    </div>
  );
}
